<script lang="ts">
export default { name: 'CalendarItem' };
</script>

<script setup lang="ts">
import { computed, inject, reactive } from 'vue';

import type { CalendarState, NormalizedCalendarItem } from './calendar-state';
import * as utils from './utils';

/**
 * `CalendarItem` component props.
 */
export interface Props {
  item: NormalizedCalendarItem;
  top: string;
}

/* eslint-disable @typescript-eslint/unified-signatures */

/**
 * `CalendarItem` component emits.
 */
export interface Emits {
  (event: 'dragstart', eventData: DragEvent): void;
  (event: 'mouseenter', eventData: MouseEvent): void;
  (event: 'mouseleave', eventData: MouseEvent): void;
  (event: 'click', eventData: MouseEvent): void;
}

/* eslint-enable @typescript-eslint/unified-signatures */

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const calendar = reactive(inject('calendarState') as CalendarState);

const elemAttrs = computed(() => {
  const values: Record<string, unknown> = {};

  if (calendar.enableDragDrop) {
    const title = props.item.tooltip ?? props.item.title;

    if (title) {
      values.title = title;
    }

    const ariaGrabbed = props.item === calendar.currentDragItem;

    if (ariaGrabbed) {
      values['aria-grabbed'] = ariaGrabbed;
    }

    values.draggable = true;
  }

  return values;
});

const displayTimeRange = computed(() => {
  const start = utils.formattedTime(
    props.item.startDate,
    calendar.displayLocale,
    calendar.timeFormatOptions,
  );

  let end: string | null = null;

  if (!utils.isSameDateTime(props.item.startDate, props.item.endDate)) {
    end = utils.formattedTime(
      props.item.endDate,
      calendar.displayLocale,
      calendar.timeFormatOptions,
    );
  }

  return { start, end };
});
</script>

<template>
  <div
    :key="item.id"
    :class="['calendar-item', ...item.classes]"
    :style="[{ top }, item.style ?? '']"
    v-bind="elemAttrs"
    @dragstart="emit('dragstart', $event)"
    @mouseenter="emit('mouseenter', $event)"
    @mouseleave="emit('mouseleave', $event)"
    @click="emit('click', $event)"
  >
    <!-- <div
      class="calendar-item-resize-handle handle-left"
      @click.prevent.stop
      @dragstart.prevent.stop="onResizeItemStart(i, $event)"
    ></div> -->

    <!-- <span>{{ itemTitle }}</span> -->
    <template v-if="calendar.showTimes">
      <span class="start-time">{{ displayTimeRange.start }}</span>
      <span v-if="displayTimeRange.end" class="end-time">{{
        displayTimeRange.end
      }}</span>
    </template>

    <span v-else>{{ item.title ?? '' }}</span>

    <!-- <div
      class="calendar-item-resize-handle handle-right"
      @click.prevent.stop
      @dragstart.prevent.stop="onResizeItemStart(i, $event)"
    ></div> -->
  </div>
</template>

<style scoped lang="scss">
.calendar-item {
  position: absolute;
  border: none !important;
  font-size: 1rem !important;
  direction: initial;
  border-radius: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  backdrop-filter: blur(20px) saturate(2);
  padding: 0.2em 0.4em;

  @include app-theme-light {
    color: #701e94;
    background-color: #d3a5decc;
  }

  @include app-theme-dark {
    color: #db67ff;
    background-color: #472a4ecc;
  }

  &.to-be-continued {
    border-right-style: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.continued {
    border-left-style: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &[draggable] {
    user-select: none;
  }
}

.calendar-item-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  background-color: #00a0ff3b;
  cursor: col-resize;

  &.handle-left {
    left: 0;
  }

  &.handle-right {
    right: 0;
  }
}

/* Item Times */
.end-time::before {
  content: '-';
}

.item-offset-0 {
  left: 0;
}

.item-offset-1 {
  left: calc((100% / 7));
}

.item-offset-2 {
  left: calc((200% / 7));
}

.item-offset-3 {
  left: calc((300% / 7));
}

.item-offset-4 {
  left: calc((400% / 7));
}

.item-offset-5 {
  left: calc((500% / 7));
}

.item-offset-6 {
  left: calc((600% / 7));
}

/* Metrics for items spanning dates */

.item-span-1 {
  width: calc((100% / 7) - 0.05em);
}

.item-span-2 {
  width: calc((200% / 7) - 0.05em);
}

.item-span-3 {
  width: calc((300% / 7) - 0.05em);
}

.item-span-4 {
  width: calc((400% / 7) - 0.05em);
}

.item-span-5 {
  width: calc((500% / 7) - 0.05em);
}

.item-span-6 {
  width: calc((600% / 7) - 0.05em);
}

.item-span-7 {
  width: calc((700% / 7) - 0.05em);
}
</style>
